import { createReducer, on } from "@ngrx/store";
import { IAutoplayOptions, IRoundState, IUserBet } from "./interfaces";
import {
    addBets,
    addRound,
    addUserBet,
    changeState,
    clearRound,
    firstHalfCashedOut,
    initialStateUpdate,
    removeAutoplayOptions,
    removeBets,
    removeUserBet,
    setAutoplayOptions,
    updateBets,
    updateMultiplier,
    userBetConfirmed
} from "./round.actions";
import { EBetSplit, ERoundState, IRoundBet, IRoundInitialState } from "../interfaces";
import { createEntityAdapter, EntityAdapter, Update } from "@ngrx/entity";
import { cloneDeep } from "lodash";

export const betsAdapter: EntityAdapter<IRoundBet> = createEntityAdapter<IRoundBet>({
    selectId: model => model.id
});

export const initialState: IRoundState = {
    roundId: undefined,
    state: ERoundState.Idle,
    bets: betsAdapter.getInitialState(),
    totalBets: 0,
    multiplier: 0,
    userBets: [],
    autoplayOptions: [],
    initialRoundState: undefined
};

const reducer = createReducer(initialState,
    on(initialStateUpdate, (state: IRoundState, props: { data: IRoundInitialState }) => {
        return {
            ...state,
            roundId: props.data.roundId,
            userBets: props.data.userBets,
            bets: betsAdapter.setAll(props.data.bets, state.bets),
            totalBets: props.data.totalBets,
            initialRoundState: props.data.initialRoundState
        };
    }),
    on(addRound, (state: IRoundState, props: { id: string }) => {
        return {
            ...state,
            roundId: props.id,
            userBets: <IUserBet[]>state.userBets.map(bet => state.roundId && bet.roundId === state.roundId ? undefined : bet).filter(b => !!b)
        };
    }),
    on(userBetConfirmed, (state: IRoundState, props: { roundId: string, betIndex: number }) => {
        const userBets: IUserBet[] = [...state.userBets];
        let betIdx: number = userBets.findIndex(bet => bet.betIndex === props.betIndex);
        if (betIdx !== -1) {
            userBets[betIdx] = {
                ...userBets[betIdx],
                multiplier: 1,
                roundId: props.roundId
            };
        }
        return {
            ...state,
            userBets
        };
    }),
    on(firstHalfCashedOut, (state: IRoundState, props: { roundId: string, betIndex: number, amount: number }) => {
        const userBets: IUserBet[] = [...state.userBets];
        let betIdx: number = userBets.findIndex(bet => bet.betIndex === props.betIndex);
        if (betIdx !== -1) {
            userBets[betIdx] = {
                ...userBets[betIdx],
                firstHalfCashedOut: props.amount
            };
        }
        return {
            ...state,
            userBets
        };
    }),
    on(clearRound, (state: IRoundState) => {
        return {
            ...state,
            ...initialState,
            roundId: state.roundId,
            userBets: state.userBets,
            autoplayOptions: state.autoplayOptions.filter(autoBet => autoBet.roundCount > 0)
        };
    }),
    on(changeState, (state: IRoundState, props: { state: ERoundState, multiplier?: number }) => {
        let updates: Update<IRoundBet>[] = [];
        const autoplayOptions: IAutoplayOptions[] = [];
        if (props.state === ERoundState.Ended) {
            const list: IRoundBet[] = betsAdapter.getSelectors().selectAll(state.bets)
                .filter(s => !s.cashout);

            updates = list.map(s => {
                return {
                    id: s.id,
                    changes: {
                        multiplier: props.multiplier
                    }
                };
            });

            for (let autoplayOpt of state.autoplayOptions) {
                const userBet: IUserBet | undefined = state.userBets.find(bet => bet.betIndex === autoplayOpt.betArgs.betIndex);
                if ((userBet && userBet.roundId) || autoplayOpt.cashOutInRound) {
                    autoplayOptions.push({
                        ...autoplayOpt,
                        roundCount: autoplayOpt.roundCount - 1,
                        cashOutInRound: false
                    });
                } else {
                    autoplayOptions.push(autoplayOpt);
                }
            }
        }
        return {
            ...state,
            state: props.state,
            multiplier: props.multiplier ?? state.multiplier,
            bets: betsAdapter.updateMany(updates, state.bets),
            autoplayOptions: autoplayOptions.length ? autoplayOptions : state.autoplayOptions
        };
    }),
    on(addBets, (state: IRoundState, props: { bets: IRoundBet[], totalBets: number }) => {
        return {
            ...state,
            bets: betsAdapter.upsertMany(props.bets, state.bets),
            totalBets: props.totalBets
        };
    }),
    on(updateBets, (state: IRoundState, props: { bets: Partial<IRoundBet>[] }) => {
        let updatedBets: IRoundBet[] = [];
        for (let bet of props.bets) {
            const stateBet: IRoundBet | undefined = state.bets.entities[bet.id!];
            if (stateBet) {
                updatedBets.push({
                    ...stateBet,
                    ...bet,
                    totalAmount: bet.totalAmount ?? (bet.split === EBetSplit.FirstHalf ? bet.cashout : ((stateBet?.cashout ?? 0) + (bet.cashout ?? 0)))
                });
            }
        }
        const updates: Update<IRoundBet>[] = updatedBets.map(b => {
            return {
                id: b.id,
                changes: b
            };
        });
        return {
            ...state,
            bets: betsAdapter.updateMany(updates, state.bets)
        };
    }),
    on(removeBets, (state: IRoundState, props: { ids: string[], totalBets: number }) => {
        return {
            ...state,
            bets: betsAdapter.removeMany(props.ids, state.bets),
            totalBets: props.totalBets
        };
    }),
    on(updateMultiplier, (state: IRoundState, props: { multiplier: number }) => {
        const userBets: IUserBet[] = cloneDeep(state.userBets);
        for (let bet of userBets) {
            if (bet.roundId && (!bet.maxMultiplier || bet.multiplier < bet.maxMultiplier)) {
                bet.accumulatedValue = bet.firstHalfCashedOut ? (bet.amount / 2) * props.multiplier : bet.amount * props.multiplier;
                bet.multiplier = props.multiplier;
            }
        }
        return {
            ...state,
            multiplier: props.multiplier,
            userBets
        };
    }),
    on(addUserBet, (state: IRoundState, props: {
        data: {
            amount: number, betIndex: number,
            maxMultiplier: number | undefined, roundId: string | undefined
        }
    }) => {
        const userBets: IUserBet[] = [...state.userBets];
        let betIdx: number = userBets.findIndex(bet => bet.betIndex === props.data.betIndex);
        if (betIdx === -1) {
            userBets.push({
                ...props.data,
                accumulatedValue: props.data.amount,
                multiplier: 1,
                roundId: props.data.roundId,
                firstHalfCashedOut: undefined
            });
        } else {
            userBets[betIdx] = {
                ...userBets[betIdx],
                roundId: undefined
            };
        }
        return {
            ...state,
            userBets
        };
    }),
    on(removeUserBet, (state: IRoundState, props: { betIndex: number, isCashOut: boolean, skipAutoplayOptionsRemoving: boolean }) => {
        const userBets: IUserBet[] = cloneDeep(state.userBets);
        const autoplayOptions: IAutoplayOptions[] = cloneDeep(state.autoplayOptions);
        const betIdx: number = state.userBets.findIndex(bet => bet.betIndex === props.betIndex);
        const autoplayOptionsBetIdx: number = state.autoplayOptions.findIndex(aopts => aopts.betArgs.betIndex === props.betIndex);
        if (betIdx !== -1) {
            userBets.splice(betIdx, 1);
        }
        if (!props.skipAutoplayOptionsRemoving && autoplayOptionsBetIdx !== -1) {
            if (!props.isCashOut) {
                autoplayOptions.splice(autoplayOptionsBetIdx, 1);
            } else {
                autoplayOptions[autoplayOptionsBetIdx].cashOutInRound = true;
            }
        }

        return {
            ...state,
            userBets,
            autoplayOptions
        };
    }),
    on(setAutoplayOptions, (state: IRoundState, props: { options: IAutoplayOptions }) => {
        return {
            ...state,
            autoplayOptions: [
                ...state.autoplayOptions,
                {
                    ...props.options,
                    initialRoundCount: props.options.roundCount
                }
            ]
        };
    }),
    on(removeAutoplayOptions, (state: IRoundState, props: { betIndex: number }) => {
        const autoplayOptsIdx: number = state.autoplayOptions.findIndex(o => o.betArgs.betIndex === props.betIndex);
        let autoplayOptsArr: IAutoplayOptions[] = [];

        if (autoplayOptsIdx === -1) {
            return state;
        }

        for (let [index, opts] of state.autoplayOptions.entries()) {
            if (index !== autoplayOptsIdx) {
                autoplayOptsArr.push(opts);
            }
        }

        return {
            ...state,
            autoplayOptions: autoplayOptsArr
        };
    })
);

export function roundReducer(state: IRoundState, action: any) {
    return reducer(state, action);
}
